/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch } from "@reduxjs/toolkit";
import AuthenticatedPage from "../../pages/auth/AuthenticatedPage";
import BackendApiInterface, { CombinedVersionInfoJson, LabelJsonWrapper } from "../backend-api-interface";
import { BackendClient, getCloudAuthBackendClient } from "../cloud-backend-auth";
import store from "../../store/store";
import { MVisionAppClient } from "../../store/configurationTarget/mvision-client-list";
import { convertResponseToError, getErrorMessageFromBackendValidationErrorJson, parseConcurrentSaveError, ConcurrentSaveErrorWrapper } from "../../util/errors";
import { parseSaveResponse, SaveResult } from "../save-response";
import apiUrls, { DEFAULT_USER_ID, getLabelingUrl } from "./backend-urls";
import { appConfigSelectors } from "../../store/appConfig/appConfigSlice";
import { convertModelTypeToApiString, convertModelTypeToExportApiString, ModelType } from "../../store/global-types/customization-types";
import { DeploymentConfigInfo } from "../../store/appConfig/appDeploymentInfoTypes";
import { WorkspaceLabeling } from "../../store/appVersion/labeling";
import { AppVersionInfo, getDefaultVersionInfo } from "../../store/appVersion/appVersionTypes";
import { BackupInfo } from "../../store/backup/backup-types";

/** Current app version. This is being sent to backend along API calls so must be set explicitly. */
let _appVersion: AppVersionInfo | undefined = undefined;

/**
 * Wrapper for accessing MVision Configuration functions through MVision cloud.
 */
export default class CloudApi implements BackendApiInterface {

    /** Returns a backend client for any backend API calls matching whatever is the current
     * cloud backend.
     */
    getAuthBackendClient(): BackendClient {
        const backend = appConfigSelectors.selectBackend(store.getState());
        if (backend === undefined) {
            throw new Error(`Could not get current cloud backend.`);
        }

        return getCloudAuthBackendClient(backend, _appVersion || getDefaultVersionInfo());
    }

    async init(dispatch: Dispatch, deploymentInfo: DeploymentConfigInfo, appVersion: AppVersionInfo | null) {
        _appVersion = appVersion || undefined;
    }

    /** Performs a GET fetch request to backend. */
    protected async fetchFromApi(url: string, headers?: any): Promise<any> {
        const client = this.getAuthBackendClient();
        const response = await client.fetch(url, { headers: headers || {} });
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = getErrorMessageFromBackendValidationErrorJson(json);
            console.error(message);
            console.log(json);
            throw new Error(message);
        }

        return json;
    }

    /** Sends a POST request to backend, with an optional JSON body and optional headers. */
    private async postToApi(url: string, headers?: any, jsonData?: string): Promise<any> {
        const client = this.getAuthBackendClient();
        const data = jsonData;

        const httpRequestOptions: any = {};
        if (jsonData) { httpRequestOptions['body'] = data; }
        if (headers) { httpRequestOptions['headers'] = headers; }
        const response = await client.post(url, httpRequestOptions, { postJson: true });
        const json = await response.json();

        if (!response || response.status !== 200) {
            // Check for concurrent save conflict BEFORE falling through to validation error parsing
            const concurrentError = parseConcurrentSaveError(json);
            if (concurrentError) {
                throw new ConcurrentSaveErrorWrapper(concurrentError);
            }

            const message = getErrorMessageFromBackendValidationErrorJson(json);
            console.error(message);
            console.log(response);
            if (jsonData) {
                throw await convertResponseToError(json, jsonData, message);
            } else {
                throw new Error(message);
            }
        }

        return json;
    }

    /** Sends a PUT request to backend, with an optional JSON body and optional headers. */
    protected async putToApi(url: string, headers?: any, jsonData?: string): Promise<any> {
        const client = this.getAuthBackendClient();
        const data = jsonData;

        const httpRequestOptions: any = { 'method': 'put' };
        if (jsonData) { httpRequestOptions['body'] = data; }
        if (headers) { httpRequestOptions['headers'] = headers; }
        const response = await client.fetch(url, httpRequestOptions, { postJson: true });
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = getErrorMessageFromBackendValidationErrorJson(json);
            console.error(message);
            console.log(response);
            if (jsonData) {
                throw await convertResponseToError(json, jsonData, message);
            } else {
                throw new Error(message);
            }
        }

        return json;
    }

    /** Performs a DELETE request to backend. */
    protected async deleteFromApi(url: string, headers: any): Promise<boolean> {
        const client = this.getAuthBackendClient();

        const response = await client.fetch(url, { headers: headers, 'method': 'delete' });
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = getErrorMessageFromBackendValidationErrorJson(json);
            console.error(message);
            console.log(json);
            throw new Error(message);
        }

        return json;
    }

    public async fetchContouringCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return null if no app client was given
            return null;
        }

        return await this.fetchFromApi(apiUrls.contouringConfig, { userId: appClient.userId });
    }

    public async saveContouringCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        if (appClient === undefined) {
            return { success: false };
        }

        return parseSaveResponse(await this.postToApi(apiUrls.contouringConfig, { userId: appClient.userId, ...(force ? { force: 'true' } : {}) }, json));
    }

    public async deleteContouringCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        if (appClient === undefined) {
            // return null if no app client was given
            return false;
        }

        return await this.deleteFromApi(apiUrls.contouringConfig, { userId: appClient.userId });
    }

    /** CLOUD: Returns default configuration for a single contouring model */
    public async fetchDefaultConfigurationForContouringModelAsync(modelName: string): Promise<any> {
        return await this.fetchFromApi(apiUrls.makeGetDefaultConfigForContourModelUrl(modelName), { userId: DEFAULT_USER_ID });
    }

    public async fetchDoseCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return null if no app client was given
            return null;
        }

        return await this.fetchFromApi(apiUrls.doseConfig, { userId: appClient.userId });
    }

    public async saveDoseCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        if (appClient === undefined) {
            return { success: false };
        }

        return parseSaveResponse(await this.postToApi(apiUrls.doseConfig, { userId: appClient.userId, ...(force ? { force: 'true' } : {}) }, json));
    }

    public async deleteDoseCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        if (appClient === undefined) {
            // return null if no app client was given
            return false;
        }

        return await this.deleteFromApi(apiUrls.doseConfig, { userId: appClient.userId });
    }

    /** CLOUD: Returns default configuration for a single dose model */
    public async fetchDefaultConfigurationForDoseModelAsync(modelName: string): Promise<any> {
        return await this.fetchFromApi(apiUrls.makeGetDefaultConfigForDoseModelUrl(modelName), { userId: DEFAULT_USER_ID });
    }

    public async fetchAdaptCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return null if no app client was given
            return null;
        }

        return await this.fetchFromApi(apiUrls.adaptConfig, { userId: appClient.userId });
    }

    public async saveAdaptCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        if (appClient === undefined) {
            return { success: false };
        }

        return parseSaveResponse(await this.postToApi(apiUrls.adaptConfig, { userId: appClient.userId, ...(force ? { force: 'true' } : {}) }, json));
    }

    public async deleteAdaptCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        if (appClient === undefined) {
            // return null if no app client was given
            return false;
        }

        return await this.deleteFromApi(apiUrls.adaptConfig, { userId: appClient.userId });
    }

    /** CLOUD: Returns default configuration for a single adapt model */
    public async fetchDefaultConfigurationForAdaptModelAsync(modelName: string): Promise<any> {
        return await this.fetchFromApi(apiUrls.makeGetDefaultConfigForAdaptModelUrl(modelName), { userId: DEFAULT_USER_ID });
    }

    public async fetchImageCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return null if no app client was given
            return null;
        }

        return await this.fetchFromApi(apiUrls.imageConfig, { userId: appClient.userId });
    }

    public async saveImageCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        if (appClient === undefined) {
            return { success: false };
        }

        return parseSaveResponse(await this.postToApi(apiUrls.imageConfig, { userId: appClient.userId, ...(force ? { force: 'true' } : {}) }, json));
    }

    public async deleteImageCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        if (appClient === undefined) {
            // return null if no app client was given
            return false;
        }

        return await this.deleteFromApi(apiUrls.imageConfig, { userId: appClient.userId });
    }

    /** CLOUD: Returns default configuration for a single image model */
    public async fetchDefaultConfigurationForImageModelAsync(modelName: string): Promise<any> {
        return await this.fetchFromApi(apiUrls.makeGetDefaultConfigForImageModelUrl(modelName), { userId: DEFAULT_USER_ID });
    }

    public async fetchAllDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            return null;
        }

        return await this.fetchFromApi(apiUrls.daemonsConfig, { userId: appClient.userId });
    }

    public async fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<any> {
        if (appClient === undefined) {
            return null;
        }

        return await this.fetchFromApi(apiUrls.daemonConfig, { userId: appClient.userId, daemonSessionId: daemonSessionId });
    }

    public async saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean> {
        if (appClient === undefined) {
            return false;
        }

        return await this.postToApi(apiUrls.daemonConfig, { userId: appClient.userId }, json);
    }

    public async regenerateCredentialsAsync(appClient: MVisionAppClient | undefined, sessionID: string): Promise<any> {
        if (appClient === undefined) {
            return null;
        }

        return await this.fetchFromApi(apiUrls.regenerateCredentials, { userId: appClient.userId, newSessionId: sessionID });
    }

    public async tokenAssociationApproveAsync(appClient: MVisionAppClient | undefined, token: string, sessionID: string): Promise<string | null> {
        if (appClient === undefined) {
            return null;
        }

        return await this.postToApi(apiUrls.tokenAssociate, { userId: appClient.userId, token, newSessionId: sessionID });
    }

    public async resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        if (appClient === undefined || !daemonSessionId) {
            return false;
        }

        return await this.postToApi(apiUrls.resetDaemonConfig, { userId: appClient.userId, daemonSessionId: daemonSessionId });
    }

    public async deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        if (appClient === undefined || !daemonSessionId) {
            return false;
        }

        return await this.deleteFromApi(apiUrls.daemonConfig, { userId: appClient.userId, daemonSessionId: daemonSessionId });
    }

    public async fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return an empty license if no app client was given
            return {};
        }

        return await this.fetchFromApi(apiUrls.getLicenseUrl, { userId: appClient.userId });
    }

    public async saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<boolean> {
        const trimmedLicense = newLicense.trim();

        if (appClient === undefined || !trimmedLicense) {
            return false;
        }
        
        const licenseJson = JSON.stringify({ license: trimmedLicense });

        return await this.postToApi(apiUrls.saveLicenseUrl, { userId: appClient.userId }, licenseJson);
    }

    public async fetchLabelingAsync(modelTypes: ModelType[]): Promise<CombinedVersionInfoJson> {
        const labelingJsons: LabelJsonWrapper[] = [];

        const backendJson = await this.fetchFromApi(apiUrls.backendVersionUrl);

        for (const modelType of modelTypes) {
            const labelingJson = await this.fetchFromApi(getLabelingUrl(modelType));
            labelingJsons.push({ modelType: modelType, labeling: labelingJson });
        }

        return { backendVersion: backendJson, labeling: labelingJsons };
    }

    public async fetchWorkspaceLabelingAsync(): Promise<WorkspaceLabeling> {
        return await this.fetchFromApi(apiUrls.workspaceLabeling);
    }

    public async fetchAccessRightsAsync(): Promise<any> {
        return await this.fetchFromApi(apiUrls.getAccessRightsUrl);
    }

    public async exportCustomizationPdfAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<Blob> {
        if (appClient === undefined) {
            throw new Error('No app client provided -- cannot export customization PDF');
        }

        const client = this.getAuthBackendClient();
        const response = await client.fetch(apiUrls.exportConfig, {
            headers: {
                userId: appClient.userId,
                modelType: convertModelTypeToExportApiString(modelType),
                exportFormat: 'pdf',
            },
        });

        if (!response || response.status !== 200) {
            const message = `Failed to export customization PDF (status ${response.status})`;
            console.error(message);
            throw new Error(message);
        }

        return await response.blob();
    }

    public async fetchBackupsAsync(appClient: MVisionAppClient | undefined, modelType: string): Promise<BackupInfo[]> {
        if (appClient === undefined) {
            return [];
        }

        return await this.fetchFromApi(apiUrls.backupsConfig, { userId: appClient.userId, modelType });
    }

    public async downloadBackupAsync(appClient: MVisionAppClient | undefined, modelType: string, backupFilename: string): Promise<any> {
        if (appClient === undefined) {
            return null;
        }

        return await this.fetchFromApi(apiUrls.backupDownload, { userId: appClient.userId, modelType, 'backup_filename': backupFilename });
    }

    public async fetchActiveModelsAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<any> {
        if (appClient === undefined) {
            // return null if no app client was given
            return null;
        }

        return await this.fetchFromApi(apiUrls.getListActiveModelsUrl, { userId: appClient.userId, modelType: convertModelTypeToApiString(modelType) });
    }

    public async fetchClientListAsync(applicationsOnly?: boolean): Promise<any> {
        const client = this.getAuthBackendClient();
        // Bootstrap flow from /#/cloud passes applicationsOnly=true so the backend returns
        // application/daemon-style targets only. Omit the header otherwise.
        const requestOptions = applicationsOnly ? { headers: { applicationsOnly: 'true' } } : undefined;
        const response = await client.fetch(apiUrls.getClientListUrl, requestOptions);
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = getErrorMessageFromBackendValidationErrorJson(json);
            console.error(message);
            console.log(json);
            throw new Error(message);
        }

        return json;
    }

    public async getUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<any> {
        if (appClient === undefined) {
            return null;
        }

        const url = apiUrls.makeCloudUserSettingUrl(appClient.userId, settingName);
        return await this.fetchFromApi(url);
    }

    public async saveUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string, jsonValue: string): Promise<boolean> {
        if (appClient === undefined) {
            return false;
        }

        const url = apiUrls.makeCloudUserSettingUrl(appClient.userId, settingName);
        return await this.putToApi(url, undefined, jsonValue);
    }

    public async deleteUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<boolean> {
        if (appClient === undefined) {
            return false;
        }
        const url = apiUrls.makeCloudUserSettingUrl(appClient.userId, settingName);
        return await this.deleteFromApi(url, {});
    }


    public getAppWrapperComponent(): (props: React.PropsWithChildren<object>) => JSX.Element {
        return AuthenticatedPage;
    }
}
